import { useState, useEffect, useRef } from 'react';
import { useCategories } from './useCategories';
import { useProducts } from './useProducts';

/**
 * Hook to track when critical Firebase data is ready
 * CRITICAL FIX: Start optimistic (isReady: true) to never block first-time users
 * Only set to false if we're actually waiting AND haven't timed out
 */
export const useFirebaseDataReady = () => {
  const { loading: categoriesLoading } = useCategories();
  const { loading: productsLoading } = useProducts();
  // CRITICAL: Start with true (optimistic) - don't block initial render
  const [isReady, setIsReady] = useState(true);
  const timeoutRef = useRef(null);
  const hasTimedOutRef = useRef(false);
  const hasSetFalseRef = useRef(false);

  useEffect(() => {
    // Set a very short timeout to mark as ready (for first-time users with slow connections)
    // Reduced to 200ms - we want to show content ASAP
    if (!timeoutRef.current && !hasTimedOutRef.current) {
      timeoutRef.current = setTimeout(() => {
        hasTimedOutRef.current = true;
        setIsReady(true); // Force ready after timeout
      }, 200); // 200ms max wait - very aggressive for first-time users
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    // Data is ready when both categories and products have finished loading
    // OPTIMIZED: Don't require categories.length > 0 - this blocks new users unnecessarily
    // Just check if loading is complete (even if empty arrays)
    const categoriesReady = !categoriesLoading; // Don't check length - just loading state
    const productsReady = !productsLoading; // Products loading is complete (even if empty)
    
    // CRITICAL FIX: Only set to false if BOTH are loading AND we haven't timed out
    // This prevents blocking when one is ready but the other isn't
    const bothLoading = categoriesLoading && productsLoading;
    
    if (hasTimedOutRef.current) {
      // Timeout elapsed - always ready
      setIsReady(true);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    } else if (bothLoading && !hasSetFalseRef.current) {
      // Both are loading - set to false only once
      hasSetFalseRef.current = true;
      setIsReady(false);
    } else if (categoriesReady && productsReady) {
      // Both are ready - mark as ready
      setIsReady(true);
      hasSetFalseRef.current = false;
      // Clear timeout if data loaded before timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
    // If one is ready but the other isn't, keep current state (don't block)
  }, [categoriesLoading, productsLoading]);

  return { isReady, categoriesLoading, productsLoading };
};

